import { useId } from "react"

import type { SolarSavingsYear } from "../solar-presentation"

const CHART_WIDTH = 1312
const CHART_HEIGHT = 380
const PAD_X = 60
const PAD_TOP = 30
const PAD_BOTTOM = 50

export function ProjectionChart({
  years,
  breakEvenYear,
  selectedYear = null,
  label,
}: {
  years: SolarSavingsYear[]
  /** Null draws no break-even marker. */
  breakEvenYear: number | null
  /** The year dial's year, marked on both lines; null marks none. */
  selectedYear?: number | null
  label: string
}): JSX.Element {
  const last = years[years.length - 1]
  // Every year: a payout can make the solar line fall, so its peak may be early.
  const maxValue = Math.max(
    ...years.flatMap((year) => [year.cumulativeUtility, year.cumulativeSolar]),
    1,
  )
  // An AI compute payout can carry the solar line below zero, past what the
  // system and bills cost; the scale extends down to show it.
  const minValue = Math.min(0, ...years.map((year) => year.cumulativeSolar))
  const span = Math.max(years.length - 1, 1)
  const xAt = (year: number): number =>
    PAD_X + ((year - 1) / span) * (CHART_WIDTH - PAD_X * 2)
  const yAt = (value: number): number =>
    CHART_HEIGHT -
    PAD_BOTTOM -
    ((value - minValue) / (maxValue - minValue)) *
      (CHART_HEIGHT - PAD_TOP - PAD_BOTTOM)
  const line = (pick: (year: (typeof years)[number]) => number): string =>
    years
      .map(
        (year, index) =>
          `${index === 0 ? `M` : `L`} ${xAt(year.year).toFixed(1)} ${yAt(pick(year)).toFixed(1)}`,
      )
      .join(` `)
  const utilityPath = line((year) => year.cumulativeUtility)
  const solarPath = line((year) => year.cumulativeSolar)
  const savedArea = `${utilityPath} ${[...years]
    .reverse()
    .map(
      (year) =>
        `L ${xAt(year.year).toFixed(1)} ${yAt(year.cumulativeSolar).toFixed(1)}`,
    )
    .join(` `)} Z`
  // Only the years solar costs less are savings: before break-even the solar
  // line runs above the utility line, and that gap stays unshaded.
  const aboveSolar = `${solarPath} L ${xAt(last?.year ?? 1).toFixed(1)} 0 L ${xAt(1).toFixed(1)} 0 Z`
  // React's ids carry colons, which a `url(#…)` reference doesn't need.
  const savedClipId = `solar-saved-${useId().replace(/:/g, ``)}`
  const axisYears = [1, 5, 10, 15, 20, years.length]
  const selected =
    selectedYear == null
      ? undefined
      : years.find((year) => year.year === selectedYear)

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="block h-auto w-full"
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id="solar-savings-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--bp-success)" stopOpacity="0.35" />
          <stop offset="1" stopColor="var(--bp-success)" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
        const y = PAD_TOP + fraction * (CHART_HEIGHT - PAD_TOP - PAD_BOTTOM)
        return (
          <line
            key={fraction}
            x1={PAD_X}
            y1={y}
            x2={CHART_WIDTH - PAD_X}
            y2={y}
            stroke="var(--bp-border)"
            strokeDasharray="2 4"
          />
        )
      })}
      {minValue < 0 ? (
        <g data-testid="solar-savings-zero-line">
          <line
            x1={PAD_X}
            y1={yAt(0)}
            x2={CHART_WIDTH - PAD_X}
            y2={yAt(0)}
            stroke="var(--bp-text-dim)"
          />
          {/* Right end: year 1's markers sit at the left. */}
          <text
            x={CHART_WIDTH - PAD_X - 12}
            y={yAt(0) - 6}
            textAnchor="end"
            fill="var(--bp-text-dim)"
            fontSize="11"
          >
            $0
          </text>
        </g>
      ) : null}
      <clipPath id={savedClipId}>
        <path d={aboveSolar} />
      </clipPath>
      <path
        d={savedArea}
        fill="url(#solar-savings-fill)"
        clipPath={`url(#${savedClipId})`}
      />
      <path
        d={utilityPath}
        fill="none"
        stroke="var(--bp-danger)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d={solarPath}
        fill="none"
        stroke="var(--bp-text-soft)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="4 4"
      />
      {last ? (
        <>
          <circle
            cx={xAt(last.year)}
            cy={yAt(last.cumulativeUtility)}
            r="6"
            fill="var(--bp-danger)"
          />
          <circle
            cx={xAt(last.year)}
            cy={yAt(last.cumulativeSolar)}
            r="6"
            fill="var(--bp-text-soft)"
          />
        </>
      ) : null}
      {breakEvenYear != null && breakEvenYear <= years.length ? (
        <>
          <line
            x1={xAt(breakEvenYear)}
            y1={PAD_TOP}
            x2={xAt(breakEvenYear)}
            y2={CHART_HEIGHT - PAD_BOTTOM}
            stroke="var(--bp-accent)"
            strokeDasharray="3 3"
            opacity="0.6"
          />
          {/* A final-year break-even labels leftward, inside the chart. */}
          <text
            x={xAt(breakEvenYear) + (breakEvenYear === years.length ? -8 : 8)}
            y={PAD_TOP + 14}
            textAnchor={breakEvenYear === years.length ? `end` : `start`}
            fill="var(--bp-accent)"
            fontSize="12"
            fontWeight="600"
          >
            Break-even · year {breakEvenYear}
          </text>
        </>
      ) : null}
      {selected ? (
        <g data-testid="solar-savings-year-marker" data-year={selected.year}>
          <line
            x1={xAt(selected.year)}
            y1={PAD_TOP}
            x2={xAt(selected.year)}
            y2={CHART_HEIGHT - PAD_BOTTOM}
            stroke="var(--bp-text-soft)"
            strokeWidth="1.5"
            opacity="0.5"
          />
          <circle
            cx={xAt(selected.year)}
            cy={yAt(selected.cumulativeUtility)}
            r="5"
            fill="var(--bp-page)"
            stroke="var(--bp-danger)"
            strokeWidth="2.5"
          />
          <circle
            cx={xAt(selected.year)}
            cy={yAt(selected.cumulativeSolar)}
            r="5"
            fill="var(--bp-page)"
            stroke="var(--bp-text-soft)"
            strokeWidth="2.5"
          />
        </g>
      ) : null}
      {axisYears.map((year) => (
        <text
          key={year}
          x={xAt(year)}
          y={CHART_HEIGHT - PAD_BOTTOM + 20}
          fill="var(--bp-text-dim)"
          fontSize="11"
          textAnchor="middle"
        >
          Yr {year}
        </text>
      ))}
    </svg>
  )
}
